<template>
	<ul>
		<li v-for="subCategory in subCategories" :key="subCategory.CategoryId">
			<div v-if="subCategory.Children?.length > 0">
				<Disclosure
					v-slot="{ open }"
					as="div"
					class="relative"
					:default-open="
						isAnyChildActive(subCategory) ||
						isActiveSubCategory(getCategoryLink(subCategory))
					"
				>
					<DisclosureButton
						:id="`nav-button-${subCategory.CategoryId}`"
						class="text-left w-full flex items-center justify-between lg:font-bold lg:text-base text-[13px]"
					>
						{{ subCategory.Name }}
						<div>
							<ChevronDownIcon
								v-if="!open"
								class="block h-4 w-4"
								aria-hidden="true"
							/>
							<ChevronUpIcon v-else class="block h-4 w-4" aria-hidden="true" />
						</div>
					</DisclosureButton>

					<transition
						enter-active-class="transition duration-200 ease-out"
						enter-from-class="opacity-0"
						enter-to-class="opacity-100"
						leave-active-class="transition duration-200 ease-out"
						leave-from-class="opacity-100"
						leave-to-class="opacity-0"
					>
						<DisclosurePanel :id="`nav-panel-${subCategory.CategoryId}`">
							<ul class="mt-2 grid gap-2">
								<li>
									<NuxtLink
										:to="getCategoryLink(subCategory)"
										class="py-3 pl-5 lg:text-base text-[13px] hover:bg-[#e2eff4] w-full flex items-center transition-all rounded-4xl lg:hover:underline rounded-3xl"
										:class="{
											'!no-underline !bg-blue  text-white': isActiveSubCategory(
												getCategoryLink(subCategory)
											)
										}"
									>
										{{
											$t("See all {category}", { category: subCategory.Name })
										}}
									</NuxtLink>
								</li>
								<li
									v-for="subSubCategory in subCategory.Children"
									:key="subSubCategory.CategoryId"
								>
									<NuxtLink
										:to="getCategoryLink(subSubCategory)"
										class="py-3 pl-5 lg:text-base text-[13px] hover:bg-[#e2eff4] w-full flex items-center transition-all lg:hover:underline rounded-3xl"
										:class="{
											'!no-underline !bg-blue  text-white': isActiveSubCategory(
												getCategoryLink(subSubCategory)
											)
										}"
									>
										{{ subSubCategory.Name }}
									</NuxtLink>
								</li>
							</ul>
						</DisclosurePanel>
					</transition>
				</Disclosure>
			</div>
			<NuxtLink
				v-else
				:to="getCategoryLink(subCategory)"
				class="lg:p-0 py-4 px-8 lg:text-lg text-[13px] lg:font-bold lg:bg-transparent bg-gray-200 lg:h-auto h-[42px] w-full lg:inline-block flex items-center justify-center lg:rounded-none rounded-4xl lg:hover:underline"
				:class="{
					'lg:underline lg:!bg-transparent !bg-blue lg:text-black text-white':
						isActiveSubCategory(getCategoryLink(subCategory))
				}"
			>
				{{ subCategory.Name }}
			</NuxtLink>
		</li>
	</ul>
</template>

<script setup lang="ts">
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/24/outline";

interface Props {
	subCategories: Category[];
}

const { subCategories } = defineProps<Props>();

const route = useRoute();
const { createCategoryLink } = useCategory();

const getCategoryLink = (category: Category) => {
	return `/${createCategoryLink({ category })}`;
};
const isActiveSubCategory = (path: string) => {
	return route.path === path;
};
const isAnyChildActive = (subCategory: Category) => {
	if (!subCategory.Children) return false;

	return subCategory.Children.some((child) =>
		isActiveSubCategory(getCategoryLink(child))
	);
};
</script>
